import { Color as PersonaColor } from '@persona-id/design-tokens';
import Color from 'color';
import { WrappedFieldProps } from 'redux-form';
import {
  BORDER_RADIUSES,
  FONT_SIZES,
  LINE_HEIGHTS,
  SPACINGS,
  TRANSITIONS,
} from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import FormGroup from 'dashboard/layouts/FormLayout/FormGroup';
import { COLORS } from 'dashboard/styles/variables';
import Radio from 'design-system/components/Radio';
import ThemeSerialized from 'lib/interfaces/ThemeSerialized';
import { ButtonType } from 'lib/interfaces/ui-components';

function innerButtonTypeStyles(type: ButtonType, theme: Partial<ThemeSerialized>) {
  const colorButtonPrimary = theme.colorButtonPrimary || theme.colorPrimary;
  const colorButtonPrimaryLightened = Color(colorButtonPrimary).mix(Color('white'), 0.2).hex();
  const colorButtonPrimaryLightened2 = Color(colorButtonPrimary).mix(Color('white'), 0.4).hex();

  const colorButtonSecondary = theme.colorButtonSecondary || theme.colorPrimary;
  const colorButtonSecondaryLightened = Color(colorButtonSecondary).mix(Color('white'), 0.2).hex();
  const colorButtonSecondaryLightened2 = Color(colorButtonSecondary).mix(Color('white'), 0.4).hex();

  const colorButtonSecondaryFill = theme.colorButtonSecondaryFill;
  const colorButtonSecondaryFillLightened =
    colorButtonSecondaryFill != null
      ? Color(colorButtonSecondaryFill).mix(Color('white'), 0.2).hex()
      : undefined;
  const colorButtonSecondaryFillLightened2 =
    colorButtonSecondaryFill != null
      ? Color(colorButtonSecondaryFill).mix(Color('white'), 0.4).hex()
      : undefined;

  switch (type) {
    // NB: default is primary
    default:
    case ButtonType.Primary:
      return css`
        background-color: ${colorButtonPrimary};
        background-image: ${theme.buttonBackgroundImage};
        border: ${theme.borderWidth} solid ${colorButtonPrimary};
        color: ${theme.colorFontButtonPrimary};

        svg:not(.pds-icon) path {
          stroke: ${theme.colorFontButtonPrimary};
        }

        ${theme?.buttonShadowStrength != null &&
        theme.buttonShadowStrength > 0 &&
        css`
          box-shadow: 0 3px 10px 0px ${PersonaColor.CoreSlate500};
        `};

        &:hover:enabled,
        &:focus:enabled {
          background-color: ${colorButtonPrimaryLightened};
          border-color: ${colorButtonPrimaryLightened};
          ${theme?.buttonShadowStrength != null &&
          theme.buttonShadowStrength > 0 &&
          css`
            box-shadow: 0 6px 15px 0px ${PersonaColor.CoreSlate500};
          `};
        }

        &:active:enabled {
          background-color: ${colorButtonPrimaryLightened2};
          border-color: ${colorButtonPrimaryLightened2};
          box-shadow: none;
        }

        &:disabled {
          background-color: ${theme.colorButtonPrimaryFillDisabled};
          border-color: ${theme.colorButtonPrimaryFillDisabled};
          background-image: none;
          box-shadow: none;
        }

        &:focus-visible {
          outline-color: ${colorButtonPrimaryLightened};
        }
      `;

    case ButtonType.Secondary:
      return css`
        border: ${theme.borderWidth} solid ${colorButtonSecondary};
        background-color: ${colorButtonSecondaryFill ??
        theme.colorModalBackground ??
        PersonaColor.PrimaryWhite};
        color: ${theme.colorFontButtonSecondary ?? colorButtonSecondary};

        svg:not(.pds-icon) path {
          stroke: ${theme.colorFontButtonSecondary ?? colorButtonSecondary};
        }

        &:hover:enabled,
        &:focus:enabled {
          border: ${theme.borderWidth} solid ${colorButtonSecondaryLightened};
          background-color: ${colorButtonSecondaryFillLightened ?? PersonaColor.CoreSlate50};
        }

        &:active:enabled {
          border: ${theme.borderWidth} solid ${colorButtonSecondaryLightened2};
          background-color: ${colorButtonSecondaryFillLightened2 ?? PersonaColor.CoreSlate200};
        }

        &:disabled {
          background-color: ${colorButtonSecondaryFill != null
            ? theme.colorButtonSecondaryFillDisabled
            : PersonaColor.PrimaryWhite};
          border-color: ${theme.colorButtonSecondaryFillDisabled};
          color: ${PersonaColor.CoreSlate500};

          svg:not(.pds-icon) path {
            stroke: ${PersonaColor.CoreSlate500};
          }
        }

        &:focus-visible {
          outline-color: ${colorButtonSecondaryLightened};
        }
      `;
  }
}

export const ButtonStyles = css<{ buttonType: ButtonType }>`
  ${(props) => innerButtonTypeStyles(props.buttonType, props.theme)}

  border-radius: ${(props) => props.theme.borderRadius ?? BORDER_RADIUSES.base};
  box-sizing: border-box;
  height: 48px;
  text-transform: ${(props) => props.theme.buttonTextTransform};
  padding: 0 ${SPACINGS.base * 2}px;
  font-size: ${FONT_SIZES.input};
  line-height: ${LINE_HEIGHTS.input};

  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  /* Text styling */
  font-weight: ${(props) => props.theme.buttonFontWeight ?? 500};
  text-decoration: none;
  white-space: nowrap;

  transition: ${TRANSITIONS.fast};

  /* Always show a transparent outline so animation isn't janky and only fades in */
  outline-color: transparent;
  outline-width: 2px;
  outline-offset: 2px;
  outline-style: solid;

  &:disabled {
    cursor: not-allowed;
  }

  &:not(:active):enabled {
    &:hover,
    &:focus {
      transform: translateY(-2px);
    }
  }
`;

const StyledFormGroup = styled(FormGroup)`
  display: flex;
`;

const Choice = styled('label')<{ isSelected: boolean; isLeft: boolean }>`
  border-style: solid;
  border-width: 1px;
  box-sizing: border-box;
  display: block;
  min-width: 0;
  padding: ${SPACINGS.inputPadding};
  width: 50%;

  display: flex;
  justify-content: space-between;
  cursor: pointer;

  ${(props) =>
    props.isSelected
      ? css`
          // Use box-shadow to ensure that inner content doesn't collapse (e.g.
          // from using border-width.
          border-color: ${COLORS.indigo80};
          box-shadow: 0 0 0 1px ${COLORS.indigo80};
        `
      : css`
          border-color: ${COLORS.gray20};
          ${props.isLeft ? '' : `border-left-color: ${COLORS.indigo80};`}
        `};
  ${(props) =>
    props.isLeft
      ? css`
          border-radius: ${(p) => p.theme.borderRadius} 0px 0px ${(p) => p.theme.borderRadius};
        `
      : css`
          border-radius: 0px ${(p) => p.theme.borderRadius} ${(p) => p.theme.borderRadius} 0px;
        `};
`;

const HiddenRadio = styled(Radio)`
  display: none;
`;

const FlatButton = styled('div')`
  ${ButtonStyles};
  margin-left: ${SPACINGS.base}px;
  width: ${SPACINGS.base * 8}px;
  height: ${SPACINGS.base * 3}px;
`;

const RaisedButton = styled(FlatButton)`
  box-shadow: 0 3px 10px 0px ${COLORS.gray20};
`;

const ButtonStylePicker = (props: WrappedFieldProps) => {
  const { input } = props;
  const currentValue = input.value;

  return (
    <StyledFormGroup>
      <Choice
        htmlFor={`${input.name}[0]`}
        isSelected={currentValue === '0' || !currentValue}
        isLeft={true}
      >
        {/* Need to specify `checked` for when the form is reset via redux reset */}
        <HiddenRadio
          {...input}
          id={`${input.name}[0]`}
          checked={currentValue === '0' || !currentValue}
          value='0'
          type='radio'
        />
        Flat
        <FlatButton buttonType={ButtonType.Primary} />
      </Choice>
      <Choice htmlFor={`${input.name}[1]`} isSelected={currentValue === '1'} isLeft={false}>
        {/* Need to specify `checked` for when the form is reset via redux reset */}
        <HiddenRadio
          {...input}
          id={`${input.name}[1]`}
          checked={currentValue === '1'}
          value='1'
          type='radio'
        />
        Raised
        <RaisedButton buttonType={ButtonType.Primary} />
      </Choice>
    </StyledFormGroup>
  );
};

export default ButtonStylePicker;
